import React, { useState } from 'react';
import axios from 'axios';
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!username.trim() || !password.trim()) {
      setErrorMessage('Enter both username and password to continue.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await axios.post('https://aaryanholidays.in/backend/login', {
        username: username.trim(),
        password
      });

      if (response.data.success) {
        onLogin();
        return;
      }

      setErrorMessage('Invalid username or password. Please try again.');
    } catch (error) {
      console.error('There was an error!', error);
      setErrorMessage('We could not sign you in right now. Please check the connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: 5, md: 6 },
        overflow: 'hidden',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
        boxShadow: '0 30px 80px rgba(2, 6, 23, 0.26)',
        backdropFilter: 'blur(26px)'
      }}
    >
      <Box
        sx={{
          px: { xs: 3, md: 4 },
          pt: { xs: 3, md: 4 },
          pb: 2.5,
          background: 'linear-gradient(135deg, rgba(14,165,233,0.12), rgba(37,99,235,0.08))',
          borderBottom: '1px solid rgba(148, 163, 184, 0.16)'
        }}
      >
        <Typography variant="overline" sx={{ color: '#2563eb', letterSpacing: 2.4, fontWeight: 800 }}>
          Secure Sign In
        </Typography>
        <Typography variant="h4" sx={{ mt: 1, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
          Welcome back
        </Typography>
        <Typography sx={{ mt: 1.25, color: '#475569', lineHeight: 1.8 }}>
          Access the control center to update tours, homepage sections, travel content, and enquiry workflows.
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Stack spacing={2.25}>
          {errorMessage ? (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {errorMessage}
            </Alert>
          ) : null}

          <TextField
            label="Username"
            placeholder="Enter your admin username"
            fullWidth
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isSubmitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonRoundedIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: alpha('#f8fafc', 0.92)
              }
            }}
          />

          <TextField
            label="Password"
            type="password"
            placeholder="Enter your password"
            fullWidth
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockRoundedIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: alpha('#f8fafc', 0.92)
              }
            }}
          />

          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Protected workspace for internal teams only.
            </Typography>
            <Link
              href="https://aaryanholidays.com/"
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{ color: '#2563eb', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              View website
            </Link>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            endIcon={
              isSubmitting ? <CircularProgress size={18} sx={{ color: '#ffffff' }} /> : <ArrowForwardRoundedIcon />
            }
            sx={{
              minHeight: 56,
              borderRadius: 3.5,
              textTransform: 'none',
              fontSize: 16,
              fontWeight: 800,
              background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
              boxShadow: '0 20px 40px rgba(37, 99, 235, 0.28)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0284c7 0%, #1d4ed8 100%)',
                boxShadow: '0 24px 44px rgba(37, 99, 235, 0.34)'
              },
              '&.Mui-disabled': {
                color: '#ffffff',
                background: 'linear-gradient(135deg, #7dd3fc 0%, #60a5fa 100%)'
              }
            }}
          >
            {isSubmitting ? 'Signing In' : 'Sign In to Admin'}
          </Button>
        </Stack>

        <Divider sx={{ my: 3, borderColor: 'rgba(148, 163, 184, 0.16)' }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
          {[
            { label: 'Content updates', value: 'Fast' },
            { label: 'Travel operations', value: 'Centralized' },
            { label: 'Brand control', value: 'Unified' }
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 3,
                border: '1px solid rgba(148, 163, 184, 0.14)',
                backgroundColor: '#ffffff'
              }}
            >
              <Typography sx={{ fontSize: 12, letterSpacing: 1.8, textTransform: 'uppercase', color: '#64748b' }}>
                {item.label}
              </Typography>
              <Typography sx={{ mt: 0.75, fontWeight: 800, color: '#0f172a' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};

export default Login;
